import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Location } from '@moon-v2/moon-share';
import { AuthService } from '@core/services/auth-service';
import { Loader } from '@shared/components/loader';
import { LocationService } from '@core/services/location-service/location-service';
import { CountryService } from '@core/services/country-service/country-service';
import { firstValueFrom } from 'rxjs';
import { CountrySelectorModal } from '@core/components/country-selector-modal/country-selector-modal';
import { UrlSyncService } from '@core/services/url-sync-service';
import { UserConnectedLocationService } from '@core/services/user-connected-location-service/user-connected-location-service';
import { ClarityService } from '@core/services/clarity-service';
import { NgxSonnerToaster, ToastOptions } from 'ngx-sonner';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Loader, CountrySelectorModal, NgxSonnerToaster],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('moon-io-web');
  // Classes stables stylées par src/styles/_toasts.scss, plutôt que les attributs internes de la lib.
  protected readonly toastOptions: ToastOptions = {
    classes: {
      toast: 'moon-toast',
      title: 'moon-toast__title',
      description: 'moon-toast__description',
      actionButton: 'moon-toast__action',
      closeButton: 'moon-toast__close',
      success: 'moon-toast--success',
      error: 'moon-toast--error',
      warning: 'moon-toast--warning',
      info: 'moon-toast--info',
      loading: 'moon-toast--loading',
    },
  };
  protected readonly authService = inject(AuthService);
  private countriesCache: Location[] = [];
  private isLoadingCountries = false;
  private readonly locationService = inject(LocationService);
  private readonly countryService = inject(CountryService);
  private readonly urlSyncService = inject(UrlSyncService);
  private readonly userConnectedLocationService = inject(UserConnectedLocationService);
  private readonly clarityService = inject(ClarityService);

  // Signal pour contrôler l'affichage du modal de sélection de pays
  protected showCountrySelector = signal(false);

  // Signal général pour savoir si l'application est prête (Auth + Countries chargés)
  protected readonly isAppReady = signal(false);

  async ngOnInit(): Promise<void> {
    // Initialise l'authentification OIDC au démarrage - DOIT se terminer avant tout le reste
    await this.authService.initialize();

    // Résout systématiquement la location propre de l'utilisateur courant (pays, région,
    // préfecture...), sans condition sur l'état actuel de `selectedCountry` — un pays déjà en
    // cache localStorage (compte précédent testé dans le même navigateur, ou simple retour sur
    // l'app) empêcherait sinon `UrlSyncService` de jamais déclencher cette résolution (son
    // fallback ne s'exécute que si `selectedCountry` est vide), laissant `userLocationChain`
    // vide et le verrouillage des niveaux inactif pour le nouvel utilisateur.
    // Borné dans le temps : initializeUserLocation() attend authService.getUserProfile()
    // (un ReplaySubject) — s'il n'émet jamais (profil jamais résolu), on ne doit pas bloquer
    // tout le démarrage de l'app indéfiniment ; le verrouillage arrivera juste en retard si sa
    // résolution se termine après le timeout (le signal `userLocationChain` reste réactif).
    await this.initializeUserLocationWithTimeout();

    // Récupérer les pays MAINTENANT, avant la navigation
    await this.getUserCountries();

    // Initialiser la synchronisation d'URL une fois que tout est prêt
    this.urlSyncService.initialize();

    // Clarity (no-op en dehors de la production, géré en interne par le service)
    this.clarityService.initialize();

    // L'application est prête à être affichée
    this.isAppReady.set(true);
  }

  private async initializeUserLocationWithTimeout(timeoutMs = 5000): Promise<void> {
    const TIMED_OUT = Symbol('timeout');
    const result = await Promise.race([
      this.userConnectedLocationService.initializeUserLocation().catch((error) => {
        console.error('Erreur initialisation location utilisateur:', error);
      }),
      new Promise<typeof TIMED_OUT>((resolve) => setTimeout(() => resolve(TIMED_OUT), timeoutMs)),
    ]);
    if (result === TIMED_OUT) {
      // Ne bloque pas le démarrage, mais laisse une trace : sans elle, un scope/verrouillage
      // manquant ressemblerait à un bug silencieux plutôt qu'à une résolution encore en cours
      // (le signal `userLocationChain` se mettra à jour dès qu'elle se termine, même après ça).
      console.warn(
        `Résolution de la location utilisateur non terminée après ${timeoutMs}ms — poursuite du démarrage sans verrouillage des niveaux pour l'instant.`,
      );
    }
  }

  private async getUserCountries(): Promise<void> {
    try {
      // Éviter les appels multiples si déjà en cours de chargement
      if (this.isLoadingCountries) {
        return;
      }

      // Vérifier si déjà en cache
      if (this.countriesCache.length > 0) {
        return; // Déjà récupéré
      }

      // Marquer comme en cours de chargement
      this.isLoadingCountries = true;

      const token = await firstValueFrom(this.authService.getAccessToken());

      if (!token) {
        console.error(' No access token available');
        return;
      }

      const response = await firstValueFrom(
        this.locationService.getAllDirect(token, {
          level: '0',
          skipInLocation: 'true',
          'order[name]': 'asc',
        }),
      );

      // getAllDirect retourne un format avec 'data'
      if (response?.data?.length > 1) {
        this.countriesCache = response.data;

        this.countryService.setCountries(response.data);

        // Vérifier si un pays est déjà sélectionné (localStorage)
        if (!this.countryService.selectedCountry()) {
          // Aucun pays sélectionné → afficher le modal
          this.showCountrySelector.set(true);
        }
      } else {
        // Cas d'un seul pays ou pas de pays, on laisse le service gérer si besoin
        // Mais ici on n'appelle plus initializeUserLocation directement car UrlSyncService s'en occupe si besoin
        // ou on pourrait l'appeler via un service dédié si c'est purement data.
        // Pour l'instant, on suppose que si < 2 pays, UrlSyncService ou la logique par défaut suffira.
        // MAIS pour garder la compatibilité avec l'ancien code qui appelait initializeUserLocation dans le else:
        // On peut s'appuyer sur le fait que UrlSyncService écoute NavigationEnd et le fera.
      }
    } catch (error) {
      console.error('❌ Error fetching countries:', error);
    } finally {
      // Toujours réinitialiser le flag à la fin
      this.isLoadingCountries = false;
    }
  }

  protected onCountrySelected(): void {
    // Fermer le modal
    this.showCountrySelector.set(false);
    // La mise à jour de l'URL est maintenant gérée par UrlSyncService via la souscription à selectedCountry$
  }
}
